import { useState } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import { startRegistration, type PublicKeyCredentialCreationOptionsJSON } from "@simplewebauthn/browser";
import { useAction } from "convex/react";
import { Check, Fingerprint, KeyRound, LockKeyhole, Smartphone } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { Button } from "../ui/button";
import { errorMessage } from "../workspace/format";
import { EmailCodeVerification } from "./EmailCodeVerification";
import { detectPasskeyPlatform, passkeyEnrollmentLabel, passkeyEnrollmentNicknameForAuthenticator, type PasskeyAuthenticator } from "./passkeyPlatform";

export function PasskeyEnrollment() {
  const passkeyPlatform = detectPasskeyPlatform(navigator.userAgent);
  const windowsPasskey = passkeyPlatform === "windows";
  const { signOut } = useAuthActions();
  const begin = useAction(api.passkeys.beginRegistration);
  const finish = useAction(api.passkeys.finishRegistration);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeAuthenticator, setActiveAuthenticator] = useState<PasskeyAuthenticator>("local");
  const isApple = passkeyPlatform === "apple";

  const enroll = async (authenticator: PasskeyAuthenticator = "local") => {
    setBusy(true);
    setError(null);
    setActiveAuthenticator(authenticator);
    try {
      if (!("PublicKeyCredential" in window)) throw new Error("This browser does not support passkeys. Open this page in Safari or Chrome on your phone or computer.");
      const request = await begin({ authenticator });
      const response = await startRegistration({ optionsJSON: JSON.parse(request.optionsJson) as PublicKeyCredentialCreationOptionsJSON });
      await finish({
        flowId: request.flowId,
        responseJson: JSON.stringify(response),
        nickname: passkeyEnrollmentNicknameForAuthenticator(authenticator, passkeyPlatform)
      });
    } catch (caught) {
      const message = errorMessage(caught);
      const domainMismatch = /securityerror|relying party|rp id|domain/iu.test(message);
      setError(message.includes("NotAllowedError")
        ? windowsPasskey
          ? authenticator === "hybrid"
            ? "Phone QR setup was canceled. Keep Bluetooth on for the PC and phone, then try again."
            : authenticator === "local"
              ? "Windows Hello setup was canceled. Try again, then choose Sign-in options and PIN."
              : "The browser passkey setup was canceled. Try again and choose an available passkey manager."
          : "Passkey setup was canceled. Complete it to enter Yodl."
        : domainMismatch
          ? "Yodl could not confirm this website for passkey setup. Reload https://phiclaw.ai/yodl/ and try again."
          : "We couldn’t create a passkey on this device. Use Safari or Chrome, then try again.");
    } finally {
      setBusy(false);
    }
  };

  if (windowsPasskey) {
    return (
      <main className="auth-screen" id="main-content">
        <section className="auth-card passkey-enrollment auth-card--methods" aria-labelledby="passkey-enrollment-title">
          <div className="brand-mark" aria-label="Yodl"><span>Y</span></div>
          <div className="passkey-enrollment__icon"><KeyRound size={30} /></div>
          <h1 id="passkey-enrollment-title">Choose how to secure Yodl</h1>
          <p className="auth-lede">Use any one option now. You can add more sign-in methods later in Account Security.</p>
          <div className="auth-methods" aria-label="Four Yodl security options">
            <Button className="auth-method" variant="primary" disabled={busy} onClick={() => { void enroll("local"); }}>
              <KeyRound size={18} /><span><strong>{busy && activeAuthenticator === "local" ? "Opening Windows Hello…" : "Windows Hello PIN or fingerprint"}</strong><small>Creates a passkey on this office PC</small></span>
            </Button>
            <Button className="auth-method" variant="secondary" disabled={busy} onClick={() => { void enroll("hybrid"); }}>
              <Smartphone size={18} /><span><strong>{busy && activeAuthenticator === "hybrid" ? "Opening phone QR…" : "Phone QR code"}</strong><small>Save or approve with iPhone, iPad, or Android</small></span>
            </Button>
            <EmailCodeVerification disabled={busy} />
            <Button className="auth-method" variant="secondary" disabled={busy} onClick={() => { void enroll("any"); }}>
              <Fingerprint size={18} /><span><strong>{busy && activeAuthenticator === "any" ? "Opening passkey manager…" : "Apple Passwords or another passkey"}</strong><small>Shown only when Windows or the browser exposes that manager</small></span>
            </Button>
          </div>
          {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
          <p className="passkey-enrollment__note">Windows controls whether its PIN has four or more digits. Yodl never sees the PIN, fingerprint, or email code after verification.</p>
          <button type="button" className="link-button" disabled={busy} onClick={() => { void signOut(); }}>Sign out</button>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-screen" id="main-content">
      <section className="auth-card passkey-enrollment" aria-labelledby="passkey-enrollment-title">
        <div className="brand-mark" aria-label="Yodl"><span>Y</span></div>
        <div className="passkey-enrollment__icon"><Fingerprint size={30} /></div>
        <h1 id="passkey-enrollment-title">Secure your Yodl account</h1>
        <p className="auth-lede">One final step: create a passkey before entering your clinic workspace.</p>
        <div className="passkey-enrollment__benefits">
          <p><Check size={16} /><span><strong>{isApple ? "Saved in Apple Passwords" : "Saved by your device"}</strong><small>{isApple ? "iCloud Keychain can securely sync it across your Apple devices." : "Your device or password manager protects and may sync it."}</small></span></p>
          <p><LockKeyhole size={16} /><span><strong>Biometric stays private</strong><small>Face ID, Touch ID, fingerprint, or your device PIN only unlocks the passkey locally.</small></span></p>
          <p><KeyRound size={16} /><span><strong>Phishing-resistant</strong><small>The passkey is bound to this exact Yodl website.</small></span></p>
        </div>
        {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
        <Button type="button" variant="primary" disabled={busy} onClick={() => { void enroll("local"); }}>
          <Fingerprint size={17} /> {busy ? "Waiting for your device…" : passkeyEnrollmentLabel(passkeyPlatform)}
        </Button>
        <p className="passkey-enrollment__note">Your password can start account recovery, but it cannot open Yodl without a passkey. Yodl never receives your fingerprint, face scan, or device PIN.</p>
        <button type="button" className="link-button" disabled={busy} onClick={() => { void signOut(); }}>Sign out</button>
      </section>
    </main>
  );
}
